import { AbstractClient, CompositionClient } from "@/app/client";
import {
  PageOptions,
  SMSAccountCreate,
  SMSAccountResponse,
  SMSAccountUpdate,
  SMSApiResponse,
  SMSConversation,
  SMSMessageList,
  SMSMessageResponse,
  SMSChatList,
  SMSChatResponse,
  SMSChatUpdate,
  SMSMessageSendRequest,
  Page,
} from "@/app/types";
import type { SMSMessagesByContactResponse } from "@/app/api/sms_contacts/client";

export default class APISMSSub extends CompositionClient {
  constructor(client: AbstractClient) {
    super(client);
  }

  // Account Management
  getAccounts = async (): Promise<SMSAccountResponse[]> => {
    let res = await this._fetch({
      url: `/sms/accounts/`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  createAccount = async (
    body: SMSAccountCreate,
  ): Promise<SMSAccountResponse> => {
    let res = await this._fetch({
      url: `/sms/accounts/`,
      method: "POST",
      body: body,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  getAccount = async (accountId: string): Promise<SMSAccountResponse> => {
    let res = await this._fetch({
      url: `/sms/accounts/${accountId}`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  updateAccount = async (
    accountId: string,
    body: SMSAccountUpdate,
  ): Promise<SMSAccountResponse> => {
    let res = await this._fetch({
      url: `/sms/accounts/${accountId}`,
      method: "PATCH",
      body: body,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  deleteAccount = async (accountId: string): Promise<SMSApiResponse> => {
    let res = await this._fetch({
      url: `/sms/accounts/${accountId}`,
      method: "DELETE",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  refreshAccount = async (accountId: string): Promise<SMSAccountResponse> => {
    let res = await this._fetch({
      url: `/sms/accounts/${accountId}/refresh`,
      method: "POST",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Health Check Operations
  checkAccountHealth = async (accountId: string): Promise<any> => {
    let res = await this._fetch({
      url: `/sms/accounts/${accountId}/health-check`,
      method: "POST",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  checkPhoneNumberHealth = async (accountId: string): Promise<any> => {
    let res = await this._fetch({
      url: `/sms/accounts/${accountId}/phone-health-check`,
      method: "POST",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Conversation Management
  getConversations = async (
    withLatest: boolean = true,
  ): Promise<SMSConversation[]> => {
    let res = await this._fetch({
      url: `/sms/conversations/`,
      method: "GET",
      params: { with_latest: withLatest },
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  getConversationMessages = async (
    phoneNumber: string,
    accountId: string,
    pageOptions: PageOptions,
  ): Promise<SMSMessageList> => {
    let res = await this._fetch({
      url: `/sms/conversations/${encodeURIComponent(phoneNumber)}/messages`,
      method: "GET",
      params: { account_id: accountId, ...pageOptions },
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  updateConversationPartner = async (
    phoneNumber: string,
    accountId: string,
    partnerId: string | null,
  ): Promise<SMSConversation> => {
    let res = await this._fetch({
      url: `/sms/conversations/${encodeURIComponent(phoneNumber)}/partner`,
      method: "PATCH",
      params: { account_id: accountId },
      body: { partner_id: partnerId },
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  deleteConversation = async (
    phoneNumber: string,
    accountId: string,
  ): Promise<void> => {
    let res = await this._fetch({
      url: `/sms/conversations/${encodeURIComponent(phoneNumber)}`,
      method: "DELETE",
      params: { account_id: accountId },
      auth: ["OAuth2PasswordBearer"],
    });
    // For 204 No Content
    if (res.ok) {
      if (res.status === 204) return;
      try {
        const jsonData = await res.json();
        return jsonData;
      } catch (e) {
        return;
      }
    }
    // If not res.ok, it's an error
    let errorPayload;
    try {
      errorPayload = await res.json();
    } catch (e) {
      throw new Error(
        `Failed to delete conversation. Status: ${res.status} ${res.statusText}`,
      );
    }
    throw {
      message: `Failed to delete conversation: ${errorPayload?.detail || res.statusText}`,
      status: res.status,
      payload: errorPayload,
    };
  };

  // Message Operations
  getMessage = async (messageId: string): Promise<SMSMessageResponse> => {
    let res = await this._fetch({
      url: `/sms/messages/${messageId}`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // SMS Chat Operations
  getChats = async (
    pageOptions: PageOptions,
    options?: {
      search?: string;
      includePreorderCounts?: boolean;
      includeArchived?: boolean;
      archivedOnly?: boolean;
      modifiedSince?: string;
    },
  ): Promise<SMSChatList> => {
    const params: Record<string, any> = { ...pageOptions };
    if (options?.search) {
      params.search = options.search;
    }
    params.include_preorder_counts = options?.includePreorderCounts ?? true;
    if (options?.includeArchived) {
      params.include_archived = true;
    }
    if (options?.archivedOnly) {
      params.archived_only = true;
    }
    if (options?.modifiedSince) {
      params.modified_since = options.modifiedSince;
    }

    let res = await this._fetch({
      url: `/sms-chats/`,
      method: "GET",
      params,
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  getChat = async (chatId: string): Promise<SMSChatResponse> => {
    let res = await this._fetch({
      url: `/sms-chats/${chatId}`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  batchFetchChats = async (chatIds: string[]): Promise<SMSChatResponse[]> => {
    let res = await this._fetch({
      url: "/sms-chats/batch-fetch",
      method: "POST",
      bodyMime: "application/json",
      body: { chat_ids: chatIds },
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  getChatMessages = async (
    chatId: string,
    pageOptions: PageOptions,
  ): Promise<SMSMessagesByContactResponse> => {
    let res = await this._fetch({
      url: `/sms-chats/${chatId}/messages`,
      method: "GET",
      params: pageOptions,
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  sendMessage = async (
    body: SMSMessageSendRequest,
  ): Promise<SMSMessageResponse> => {
    let res = await this._fetch({
      url: `/sms/messages/send`,
      method: "POST",
      body: {
        account_id: body.accountId,
        to_number: body.toNumber,
        content: body.content,
      },
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  markChatAsRead = async (chatId: string): Promise<SMSChatResponse> => {
    let res = await this._fetch({
      url: `/sms-chats/${chatId}/mark-read`,
      method: "PUT",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  /**
   * Mark all SMS chats as read for the current user
   */
  markAllChatsAsRead = async (): Promise<{
    marked_count: number;
    message: string;
  }> => {
    let res = await this._fetch({
      url: `/sms-chats/mark-all-read`,
      method: "POST",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  /**
   * Archive an SMS chat (soft delete)
   */
  archiveChat = async (chatId: string): Promise<SMSChatResponse> => {
    let res = await this._fetch({
      url: `/sms-chats/${chatId}/archive`,
      method: "POST",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  /**
   * Unarchive an SMS chat
   */
  unarchiveChat = async (chatId: string): Promise<SMSChatResponse> => {
    let res = await this._fetch({
      url: `/sms-chats/${chatId}/unarchive`,
      method: "POST",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  /**
   * Archive multiple SMS chats in batch
   */
  archiveChatsBatch = async (
    chatIds: string[],
  ): Promise<{
    archived_count: number;
    archived: string[];
    errors: Array<{ chat_id: string; error: string }>;
  }> => {
    let res = await this._fetch({
      url: `/sms-chats/archive-batch`,
      method: "POST",
      body: { chat_ids: chatIds },
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json"))
      return res.json();
    throw new Error("Bad content type");
  };

  postMessagesIdTriggerWorkflow = async (
    messageId: string,
    body?: { workflow_id?: string; instructions?: string },
  ): Promise<any> => {
    let res = await this._fetch({
      url: `/sms/messages/${messageId}/trigger-workflow`,
      method: "POST",
      auth: ["OAuth2PasswordBearer"],
      body: body,
      bodyMime: body ? "application/json" : undefined,
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  patchChatsIdAiContext = async (
    chatId: string,
    body: { ai_context: string | null },
  ): Promise<any> => {
    let res = await this._fetch({
      url: `/sms/chats/${chatId}/ai-context`,
      method: "PATCH",
      auth: ["OAuth2PasswordBearer"],
      body: body,
      bodyMime: "application/json",
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  updateChat = async (
    chatId: string,
    body: SMSChatUpdate,
  ): Promise<SMSChatResponse> => {
    let res = await this._fetch({
      url: `/sms-chats/${chatId}`,
      method: "PUT",
      auth: ["OAuth2PasswordBearer"],
      body: body,
      bodyMime: "application/json",
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Media serving endpoint
  getMedia = async (filename: string): Promise<Blob> => {
    let res = await this._fetch({
      url: `/sms/media/${filename}`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    return res.blob();
  };

  /**
   * Share or unshare an SMS chat with a group
   */
  shareChatWithGroup = async (
    chatId: string,
    groupId: string | null,
  ): Promise<SMSChatResponse> => {
    let res = await this._fetch({
      url: `/sms-chats/${chatId}/share-group`,
      method: "POST",
      body: { group_id: groupId },
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Disable SMS chat with cascade (delete messages and shares)
  disableChatCascade = async (
    chatId: string,
  ): Promise<{ success: boolean; message: string }> => {
    let res = await this._fetch({
      url: `/sms-chats/${chatId}/disable`,
      method: "PUT",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };
}
